import Icon from '../ui/Icon';
import UserRow, { NUMBER_COL, STICKY_CELL, USER_COL } from './UserRow';

const TH = 'px-4 py-2 text-left text-[11px] leading-[14px] font-semibold text-on-surface-variant uppercase tracking-wider whitespace-nowrap';
const STICKY_HEAD = 'sticky z-20 bg-surface-container-low';

/** Column header that sorts on click; exposes the state through aria-sort. */
function SortableHeader({ label, field, params, onSort, className = '' }) {
  const active = params.sortBy === field;
  const ariaSort = active ? (params.sortOrder === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" aria-sort={ariaSort} className={`${TH} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot hover:text-on-surface ${
          active ? 'text-primary-container' : ''
        }`}
      >
        {label}
        <Icon
          name={active ? (params.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
          size={14}
          className={active ? '' : 'opacity-40'}
        />
      </button>
    </th>
  );
}

function SkeletonRows({ count }) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={i} className="h-14 border-b border-surface-container animate-pulse" aria-hidden="true">
      <td className={`${STICKY_CELL} ${NUMBER_COL} px-4`}>
        <div className="h-3 w-5 mx-auto bg-surface-container rounded" />
      </td>
      <td className={`${STICKY_CELL} ${USER_COL} px-4`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-surface-container" />
          <div className="space-y-1.5">
            <div className="h-3 w-28 bg-surface-container rounded" />
            <div className="h-2.5 w-20 bg-surface-container-low rounded" />
          </div>
        </div>
      </td>
      {[180, 110, 90, 90, 70, 110].map((w, j) => (
        <td key={j} className="px-4">
          <div className="h-3 bg-surface-container-low rounded" style={{ width: w }} />
        </td>
      ))}
    </tr>
  ));
}

/**
 * The users grid. Scrolls horizontally on narrow screens with # and User pinned left.
 * While `refreshing`, the previous rows stay visible but dimmed.
 */
export default function UsersTable({ rows, params, onSort, getActions, loading, refreshing, skeletonCount = 8 }) {
  return (
    <div className="w-full overflow-x-auto" aria-busy={loading || refreshing}>
      <table className="w-full min-w-[1120px] text-left border-collapse">
        <caption className="sr-only">Registered users</caption>
        <thead>
          <tr className="h-9 bg-surface-container-low border-b border-outline-variant/30">
            <th scope="col" className={`${TH} ${STICKY_HEAD} ${NUMBER_COL} text-center`}>
              #
            </th>
            <SortableHeader label="User" field="name" params={params} onSort={onSort} className={`${STICKY_HEAD} ${USER_COL}`} />
            <SortableHeader label="Email ID" field="email" params={params} onSort={onSort} />
            <th scope="col" className={TH}>
              Phone Number
            </th>
            <th scope="col" className={TH}>
              Vehicle Fuel Type
            </th>
            <SortableHeader label="Total Scans" field="scansMade" params={params} onSort={onSort} />
            <th scope="col" className={TH}>
              Rewards Earned
            </th>
            <th scope="col" className={`${TH} text-right pr-6`}>
              Status &amp; Actions
            </th>
          </tr>
        </thead>
        <tbody className={`transition-opacity ${refreshing ? 'opacity-50' : ''}`}>
          {loading ? (
            <SkeletonRows count={skeletonCount} />
          ) : (
            rows.map((user) => <UserRow key={user.key} user={user} actions={getActions(user)} />)
          )}
        </tbody>
      </table>
    </div>
  );
}
